import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store"; // Assuming your store file is named 'store.ts'
import { Drawer, Spin, Table } from "antd";
import moment from "moment";

import { fetchData } from "../store/actions/cms";
import { PATH_API_MANAGE } from "../config";
import { useState } from "react";

import { Coins, DollarSign, Package, Zap, Clock } from "lucide-react";
import {
  DashPanel,
  DashMetricCard,
  DashQuickAction,
  DashTile,
} from "../components/DashboardWidgets";
import { useQuery } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { useNavigate } from "react-router-dom";
import useSocket, { useConnectedScreens } from "../hooks/useSocket";
import ModalScreenManagement from "../components/ModalScreenManagement";
import SocketLogModal from "../components/SocketLogModal";
import { useAppTheme } from "../theme/AppTheme";
import { paletteFor, tileSurface } from "../config/dashboardTheme";

interface OwnProps {
  // Define any props specific to CorePage here
}

type CorePageProps = OwnProps & PropsFromRedux;

const DRILL_TITLES: Record<string, string> = {
  today: "Activated Today",
  week: "Activated This Week",
  month: "Activated This Month",
  active: "Active Boxes",
  inactive: "Inactive Packs",
};

const CorePage: React.FC<CorePageProps> = ({ loading, fetchData, data }) => {
  const navigate = useNavigate();
  const { sendMessage, isConnected } = useSocket();
  const connectedScreens = useConnectedScreens();
  const screenConnected = connectedScreens.length > 0;
  const [showScreenModal, setShowScreenModal] = useState(false);
  const [showSocketLog, setShowSocketLog] = useState(false);
  const [drillType, setDrillType] = useState<string | null>(null);

  // The store's chosen theme. Shared with the rest of the app via AppTheme, so
  // previewing a preset here restyles everything, not just this page.
  const { preset: dashPreset, mode: colorMode } = useAppTheme();
  const palette = paletteFor(dashPreset, colorMode);

  const {
    data: storeData,
    // isPending,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["api/dashboard/store/stats"],
    // queryFn: ({ signal, queryKey }) => fetchEvents({ signal, ...queryKey[1] }),
    queryFn: () => queryApi("api/dashboard/store/stats", "GET"),
    staleTime: Infinity,
    gcTime: 10,
  });

  const { data: drillData, isFetching: isDrillFetching } = useQuery({
    queryKey: ["api/dashboard/store/stats/drill", drillType],
    queryFn: () =>
      queryApi(`api/dashboard/store/stats/drill?type=${drillType}`, "GET"),
    enabled: !!drillType,
    staleTime: 0,
    gcTime: 0,
  });

  const drillColumns = [
    {
      title: <div style={{ minWidth: "max-content" }}>Box #</div>,
      dataIndex: "slot_number",
      key: "slot_number",
      render: (slot_number: any) => <div>#{slot_number}</div>,
      width: 70,
      sorter: (a: any, b: any) => (a.slot_number ?? 0) - (b.slot_number ?? 0),
    },
    {
      title: "Name",
      dataIndex: "lottery_name",
      key: "lottery_name",
      sorter: (a: any, b: any) =>
        (a.lottery_name ?? "").localeCompare(b.lottery_name ?? ""),
      render: (_: any, record: any) => <div>{record.lottery_name}</div>,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Game-Pack #</div>,
      dataIndex: "game_number",
      key: "game_number",
      sorter: (a: any, b: any) =>
        (a.game_number ?? 0) - (b.game_number ?? 0) ||
        (a.pack ?? 0) - (b.pack ?? 0),
      render: (_: any, record: any) => (
        <div style={{ minWidth: "max-content" }}>
          {record.game_number}-{record.pack}
        </div>
      ),
      width: 320,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Start #</div>,
      dataIndex: "start_no",
      key: "start_no",
      width: 100,
      sorter: (a: any, b: any) => (a.start_no ?? 0) - (b.start_no ?? 0),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Current #</div>,
      dataIndex: "current_no",
      key: "current_no",
      width: 100,
      sorter: (a: any, b: any) => (a.current_no ?? 0) - (b.current_no ?? 0),
    },
    {
      title: "Value",
      dataIndex: "amount",
      key: "amount",
      sorter: (a: any, b: any) => (a.amount ?? 0) - (b.amount ?? 0),
      render: (amount: any) => <div>${amount}</div>,
      width: 100,
    },
    {
      title: "Sale",
      dataIndex: "sale_amount",
      key: "sale_amount",
      sorter: (a: any, b: any) => (a.sale_amount ?? 0) - (b.sale_amount ?? 0),
      render: (sale_amount: any) => <div>${sale_amount}</div>,
      width: 100,
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: any) =>
        record.is_paused === 1 ? (
          <div>Paused</div>
        ) : record.is_sold === 1 ? (
          <div>Sold</div>
        ) : (
          <div>Active</div>
        ),
      width: 100,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Activated At</div>,
      dataIndex: "activated_at",
      key: "activated_at",
      sorter: (a: any, b: any) =>
        moment(a.activated_at).valueOf() - moment(b.activated_at).valueOf(),
      render: (activated_at: any) =>
        activated_at ? (
          <div>{moment(activated_at).format("MMM DD, YYYY hh:mm A")}</div>
        ) : (
          <div>-</div>
        ),
      width: 180,
    },
  ];

  if (isLoading)
    return (
      <div
        className="w-full h-full flex justify-center items-center"
        style={{ background: palette.pageBg }}
      >
        <Spin />
      </div>
    );

  return (
    <section
      id="dashboard"
      style={{ gap: "var(--dash-gap)" }}
      className="page-section flex-1 min-h-0 flex flex-col p-3 sm:p-4 overflow-hidden"
    >
      {/* Quick Actions */}
      <DashPanel
        title="Quick Actions"
        className="flex-[1.2]"
        action={
          <span
            onClick={() => setShowSocketLog(true)}
            className="flex items-center gap-1.5 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
            style={{ color: "var(--dash-muted)" }}
            title="Connected screens — click to view socket messages"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
              style={{
                backgroundColor: screenConnected ? "#22c55e" : "#6b7280",
                boxShadow: screenConnected ? "0 0 6px #22c55e" : "none",
              }}
            />
            {`${connectedScreens.length} screen${
              connectedScreens.length === 1 ? "" : "s"
            } connected`}
          </span>
        }
      >
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 auto-rows-fr h-full"
          style={{ gap: "var(--dash-gap)" }}
        >
          <DashQuickAction
            title="Manage Inventory"
            description="View and update tickets"
            icon={<Package className="h-5 w-5" />}
            onClick={() => navigate("/inventory")}
          />
          <DashQuickAction
            title="Active Packs"
            description="Manage box assignments"
            icon={<Coins className="h-5 w-5" />}
            onClick={() => navigate("/packs")}
          />
          <DashQuickAction
            title="Live Display"
            description="Open live display"
            icon={<Zap className="h-5 w-5" />}
            onClick={() => setShowScreenModal(true)}
          />
          <DashQuickAction
            title="Current Shift"
            description="Manage current shift sales"
            icon={<Clock className="h-5 w-5" />}
            onClick={() => navigate("/startshift")}
            variant="blue"
          />
          <DashQuickAction
            title="End Shift"
            description="End current shift"
            icon={<Clock className="h-5 w-5" />}
            onClick={() => navigate("/endshift")}
            variant="red"
          />
        </div>
      </DashPanel>

      {/* Key Metrics */}
      <div
        className="grid grid-cols-2 flex-1 min-h-0"
        style={{ gap: "var(--dash-gap)" }}
      >
        <DashMetricCard
          title="Instant Sales ( Today )"
          // @ts-ignore
          value={`$${storeData?.total_sale || "0"}`}
          icon={<DollarSign className="h-6 w-6" />}
          trend="up"
        />
        <DashMetricCard
          // @ts-ignore
          value={`${storeData?.total_active}`}
          title="Active Boxes"
          icon={<Package className="h-6 w-6" />}
          trend="up"
          clickable
          onClick={() => setDrillType("active")}
        />
      </div>

      <div
        className="grid grid-cols-2 md:grid-cols-4 flex-1 min-h-0"
        style={{ gap: "var(--dash-gap)" }}
      >
        <DashMetricCard
          title="Activated Today"
          // @ts-ignore
          value={`${storeData?.tc_today}`}
          clickable
          onClick={() => setDrillType("today")}
        />
        <DashMetricCard
          title="Activated This Week"
          // @ts-ignore
          value={`${storeData?.tc_week}`}
          clickable
          onClick={() => setDrillType("week")}
        />
        <DashMetricCard
          title="Activated This Month"
          // @ts-ignore
          value={`${storeData?.tc_month}`}
          clickable
          onClick={() => setDrillType("month")}
        />
        <DashMetricCard
          title="Inactive Packs"
          // @ts-ignore
          value={`${storeData?.tc_total}`}
          clickable
          onClick={() => setDrillType("inactive")}
        />
      </div>

      {/* Tickets on Screen */}
      <DashPanel title="Tickets on Screen" className="flex-[1.4]">
        <div
          className="grid grid-cols-5 md:grid-cols-10 auto-rows-fr h-full"
          style={{ gap: "var(--dash-gap)" }}
        >
          {[1, 2, 3, 5, 7, 10, 20, 25, 30, 50].map((amount) => (
            <DashTile
              key={amount}
              onClick={() => sendMessage("event_screen_amount", amount)}
              surface={tileSurface(
                dashPreset,
                palette.amountBg,
                palette.amountText,
              )}
            >
              ${amount}
            </DashTile>
          ))}
        </div>
      </DashPanel>

      {/* Tickets Types */}
      <DashPanel title="Tickets Types" className="flex-1">
        <div
          className="grid grid-cols-3 auto-rows-fr h-full"
          style={{ gap: "var(--dash-gap)" }}
        >
          <DashTile
            onClick={() => sendMessage("event_screen_ticket_type", 1)}
            surface={tileSurface(
              dashPreset,
              palette.typeNewBg,
              palette.typeNewText,
            )}
          >
            New
          </DashTile>
          <DashTile
            onClick={() => sendMessage("event_screen_ticket_type", 2)}
            surface={tileSurface(
              dashPreset,
              palette.typeLuckyBg,
              palette.typeLuckyText,
            )}
          >
            Lucky
          </DashTile>
          <DashTile
            onClick={() => sendMessage("event_screen_ticket_type", 3)}
            surface={tileSurface(
              dashPreset,
              palette.typeEndingBg,
              palette.typeEndingText,
            )}
          >
            Ending
          </DashTile>
        </div>
      </DashPanel>

      <ModalScreenManagement
        showModal={showScreenModal}
        toggleModal={setShowScreenModal}
      />

      <SocketLogModal
        open={showSocketLog}
        onClose={() => setShowSocketLog(false)}
        isConnected={isConnected}
      />


      <Drawer
        title={
          drillType
            ? `${DRILL_TITLES[drillType] ?? "Details"} (${
                (drillData as any)?.count ?? 0
              })`
            : "Details"
        }
        width={1000}
        onClose={() => setDrillType(null)}
        open={!!drillType}
      >
        {isDrillFetching ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spin />
          </div>
        ) : (
          <Table
            columns={drillColumns}
            dataSource={(drillData as any)?.data ?? []}
            rowKey={(record: any) => record.slot_id}
            size="small"
            sticky
            tableLayout="auto"
            pagination={false}
          />
        )}
      </Drawer>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: state?.cms?.dashboard ?? {},
  loading: state.notification.isLoading,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchData: (reqData: any) =>
    dispatch(
      fetchData(PATH_API_MANAGE, `dashboard/admin`, reqData, "dashboard"),
    ),
});
const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(CorePage);
